import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiError, useApiFetch } from "../../api/client";
import { useAppUser } from "../../app/AppUserContext";
import { ClientCombobox } from "../../components/ClientCombobox";

interface Client {
  clientId: number;
  clientName: string;
  clientCode: string | null;
  status: string;
}

type BatchStatus = "Uploading" | "Parsing" | "Review" | "Committing" | "Committed" | "Failed" | "Discarded";

interface ValidationIssue {
  field: string;
  severity: "Error" | "Warning";
  code: string;
  message: string;
}

interface ColumnMappingEntry {
  sheetRole: string;
  sourceOrdinal: number;
  incomingHeader: string | null;
  targetField: string | null;
  matchKind: "Exact" | "Alias" | "Absent" | "Unmapped" | "OverriddenByUser";
}

interface SpendBatchSummary {
  totalRows: number;
  validRows: number;
  warningRows: number;
  errorRows: number;
  excludedRows: number;
  periodStart: string | null;
  periodEnd: string | null;
  distinctSuppliers: number;
  distinctTradeNames: number;
  unresolvedSuppliers: number;
  unresolvedTradeNames: number;
}

interface SpendBatchDetail {
  batchGuid: string;
  clientId: number;
  clientName: string;
  status: BatchStatus;
  statusDetail: string | null;
  fileName: string;
  fileSizeBytes: number;
  uploadedUtc: string;
  committedUtc: string | null;
  createdByAppUserId: number;
  summary: SpendBatchSummary;
  columnMapping: ColumnMappingEntry[];
  canCommit: boolean;
  blockers: ValidationIssue[];
}

interface SpendStagedRow {
  sourceRowNumber: number;
  raw: Record<string, string | null>;
  resolved: {
    supplierId: number | null;
    supplierNameRaw: string | null;
    gradeId: number | null;
    tradeNameRaw: string | null;
    invoiceDate: string | null;
    quantity: number | null;
    unitPrice: number | null;
    currencyCode: string | null;
    purchaseTypeCode: string | null;
  };
  validationState: "Valid" | "Warning" | "Error" | "Excluded";
  issues: ValidationIssue[];
  isEdited: boolean;
  isExcluded: boolean;
}

const MAPPING_CHIP: Record<ColumnMappingEntry["matchKind"], { cls: string; icon: string; label: string }> = {
  Exact: { cls: "ok", icon: "check", label: "Exact" },
  Alias: { cls: "info", icon: "swap_horiz", label: "Alias match" },
  Absent: { cls: "", icon: "remove", label: "Absent, optional" },
  Unmapped: { cls: "bad", icon: "block", label: "Not in schema, dropped" },
  OverriddenByUser: { cls: "info", icon: "edit", label: "Overridden" },
};

export function SpendLane() {
  const apiFetch = useApiFetch();
  const { appRole, appUserId } = useAppUser();
  const canManage = (createdByAppUserId: number) =>
    appRole === "Admin" || (appRole === "Uploader" && createdByAppUserId === appUserId);

  const [clientQuery, setClientQuery] = useState("");
  const [comboMatchCount, setComboMatchCount] = useState(0);
  const [duplicateCandidates, setDuplicateCandidates] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [creatingClient, setCreatingClient] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);

  const [file, setFile] = useState<File | { name: string } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [batch, setBatch] = useState<SpendBatchDetail | null>(null);
  const [rows, setRows] = useState<SpendStagedRow[]>([]);
  const [rowState, setRowState] = useState<"All" | "Valid" | "Warning" | "Error" | "Excluded">("All");
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const resumeGuid = searchParams.get("resume");
    if (!resumeGuid) return;
    setSearchParams((p) => { p.delete("resume"); return p; }, { replace: true });
    (async () => {
      try {
        const res = await apiFetch(`spend/batches/${resumeGuid}`);
        const b: SpendBatchDetail = await res.json();
        setSelectedClient({ clientId: b.clientId, clientName: b.clientName, clientCode: null, status: "Active" });
        setFile({ name: b.fileName });
        setBatch(b);
        if (b.status === "Review" || b.status === "Committed") await loadRows(resumeGuid, "All");
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createClient = async () => {
    setCreatingClient(true);
    setClientError(null);
    try {
      const res = await apiFetch("clients", { method: "POST", body: JSON.stringify({ clientName: clientQuery.trim() }) });
      const created: Client = await res.json();
      setSelectedClient(created);
      setDuplicateCandidates([]);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const candidates = (err.body as { detail?: { candidates: Client[] } })?.detail?.candidates ?? [];
        setDuplicateCandidates(candidates);
        setClientError("A client with a similar name already exists. Pick one below, or use a more specific name.");
      } else {
        setClientError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setCreatingClient(false);
    }
  };

  const loadRows = async (batchGuid: string, state: typeof rowState) => {
    const query = state === "All" ? "" : `&state=${state}`;
    const res = await apiFetch(`spend/batches/${batchGuid}/rows?page=1&pageSize=500${query}`);
    const page: { rows: SpendStagedRow[] } = await res.json();
    setRows(page.rows);
  };

  const pollBatch = async (batchGuid: string) => {
    for (let i = 0; i < 40; i++) {
      const res = await apiFetch(`spend/batches/${batchGuid}`);
      const b: SpendBatchDetail = await res.json();
      setBatch(b);
      if (b.status === "Review" || b.status === "Failed" || b.status === "Committed") {
        if (b.status === "Review" || b.status === "Committed") await loadRows(batchGuid, rowState);
        return;
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    setError("Parsing is taking longer than expected. Reload to check again.");
  };

  const loadFile = async (f: File) => {
    if (!selectedClient) return;
    setFile(f);
    setUploading(true);
    setError(null);
    setBatch(null);
    setRows([]);
    try {
      const form = new FormData();
      form.append("file", f);
      form.append("clientId", String(selectedClient.clientId));
      const res = await apiFetch("spend/batches", { method: "POST", body: form });
      const created: SpendBatchDetail = await res.json();
      await pollBatch(created.batchGuid);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const detail = (err.body as { detail?: { existingBatchGuid?: string } })?.detail;
        if (detail?.existingBatchGuid) {
          setError("This exact file has already been uploaded — showing the existing batch instead.");
          const existingRes = await apiFetch(`spend/batches/${detail.existingBatchGuid}`);
          const existing: SpendBatchDetail = await existingRes.json();
          setBatch(existing);
          if (existing.status === "Review" || existing.status === "Committed") {
            await loadRows(detail.existingBatchGuid, rowState);
          }
        } else {
          setError("This exact file has already been uploaded.");
        }
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setUploading(false);
    }
  };

  const commit = async () => {
    if (!batch) return;
    setCommitting(true);
    try {
      const expectedRowCount = batch.summary.validRows + batch.summary.warningRows;
      const excludeErrorRows = batch.summary.errorRows > 0;
      const res = await apiFetch(`spend/batches/${batch.batchGuid}/commit`, {
        method: "POST",
        body: JSON.stringify({ expectedRowCount, excludeErrorRows }),
      });
      setBatch(await res.json());
      for (let i = 0; i < 20; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const check = await apiFetch(`spend/batches/${batch.batchGuid}`);
        const b: SpendBatchDetail = await check.json();
        setBatch(b);
        if (b.status !== "Committing") break;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCommitting(false);
    }
  };

  const discard = async () => {
    if (!batch) return;
    try {
      await apiFetch(`spend/batches/${batch.batchGuid}`, { method: "DELETE" });
      setFile(null);
      setBatch(null);
      setRows([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const changeRowState = async (state: typeof rowState) => {
    setRowState(state);
    if (batch) await loadRows(batch.batchGuid, state);
  };

  return (
    <section className="lane on">
      <h2>Client Purchase File</h2>
      <p className="lede">
        Upload a completed RESIN8 intake workbook. Columns are matched against
        the purchase schema and held for review before anything is committed.
      </p>

      <div className="sec">
        <div className="card narrow">
          <label className="fl" htmlFor="client-search">Client<span className="req">*</span></label>
          {selectedClient ? (
            <div className="chip ok" style={{ fontSize: 14, padding: "6px 12px" }}>
              <span className="material-symbols-sharp">check</span>
              {selectedClient.clientName}
              <button className="linkbtn" style={{ marginLeft: 8 }} onClick={() => { setSelectedClient(null); setFile(null); setBatch(null); }}>
                Change
              </button>
            </div>
          ) : (
            <>
              <ClientCombobox
                id="client-search"
                selected={selectedClient}
                onSelect={(c) => { setSelectedClient(c); setDuplicateCandidates([]); setClientError(null); }}
                onQueryChange={setClientQuery}
                onMatchesChange={(m) => setComboMatchCount(m.length)}
              />
              {duplicateCandidates.length > 0 && (
                <div className="card raised" style={{ marginTop: 8, padding: 8 }}>
                  {duplicateCandidates.map((c) => (
                    <button
                      key={c.clientId} className="qitem"
                      onClick={() => { setSelectedClient(c); setDuplicateCandidates([]); setClientError(null); }}
                    >
                      {c.clientName}{c.clientCode ? ` (${c.clientCode})` : ""}
                    </button>
                  ))}
                </div>
              )}
              {clientQuery.trim() && comboMatchCount === 0 && (
                <div style={{ marginTop: 8 }}>
                  <button className="btn ghost" disabled={creatingClient} onClick={createClient}>
                    <span className="material-symbols-sharp">add</span>
                    {creatingClient ? "Creating…" : `Create New Client "${clientQuery.trim()}"`}
                  </button>
                </div>
              )}
              {clientError && <p className="hint" style={{ color: "var(--salmon)" }}>{clientError}</p>}
            </>
          )}
        </div>
      </div>

      {selectedClient && !file && appRole === "Viewer" && (
        <div className="sec">
          <div className="gate narrow">
            <span className="msg">Your account has view-only access. Ask an Admin for Uploader access to upload files.</span>
          </div>
        </div>
      )}

      {selectedClient && !file && appRole !== "Viewer" && (
        <div className="sec">
          <div
            className={`drop${dragging ? " hot" : ""}`}
            onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
            onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) loadFile(f); }}
          >
            <span className="material-symbols-sharp">upload_file</span>
            <h3>Drop the Intake Workbook Here</h3>
            <p>.xlsx, up to 60 MB</p>
            <button className="btn" onClick={() => inputRef.current?.click()}>
              <span className="material-symbols-sharp">folder_open</span>Choose a File
            </button>
            <input ref={inputRef} type="file" accept=".xlsx,.xlsm" onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFile(f); e.target.value = ""; }} />
          </div>
        </div>
      )}

      {error && (
        <div className="sec"><div className="gate narrow"><span className="msg" style={{ color: "var(--salmon)" }}>{error}</span></div></div>
      )}

      {file && (uploading || batch) && (
        <div className="sec">
          <div className="card narrow">
            <div className="filehead">
              <div className="fi">
                <div className="fname"><span className="material-symbols-sharp" style={{ color: "var(--lime)" }}>description</span><span>{file.name}</span></div>
                <div className="fmeta">
                  {uploading && !batch
                    ? "Uploading…"
                    : batch?.status === "Parsing"
                      ? "Parsing…"
                      : batch
                        ? `Batch ${batch.batchGuid.slice(0, 8)}`
                        : ""}
                </div>
              </div>
              <button className="btn ghost" disabled={uploading} onClick={() => { setFile(null); setBatch(null); setRows([]); }}>
                <span className="material-symbols-sharp">close</span>Remove File
              </button>
            </div>
            {(uploading || batch?.status === "Parsing") && (
              <div className="factgrid">
                <div className="fact">
                  <div className="v"><span className="material-symbols-sharp spin">progress_activity</span></div>
                  <div className="k">{uploading && !batch ? "Uploading the workbook…" : "Parsing the workbook…"}</div>
                </div>
              </div>
            )}
            {batch && batch.status !== "Parsing" && (
              <div className="factgrid">
                <div className="fact"><div className="v">{batch.summary.totalRows}</div><div className="k">Total rows</div></div>
                <div className="fact"><div className="v">{batch.summary.validRows}</div><div className="k">Valid rows</div></div>
                <div className="fact"><div className="v">{batch.summary.errorRows}</div><div className="k">Rows with errors</div></div>
                <div className="fact"><div className="v">{batch.status}</div><div className="k">Status</div></div>
              </div>
            )}
          </div>
        </div>
      )}

      {batch && batch.columnMapping.length > 0 && (
        <div className="sec">
          <div className="sec-hd"><h3>Column Mapping</h3></div>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <table>
              <thead><tr><th style={{ width: "34%" }}>Incoming header</th><th style={{ width: "34%" }}>Schema field</th><th>Result</th></tr></thead>
              <tbody>
                {batch.columnMapping.map((m) => {
                  const meta = MAPPING_CHIP[m.matchKind];
                  return (
                    <tr key={m.sourceOrdinal}>
                      <td>{m.incomingHeader ?? "—"}</td>
                      <td>{m.targetField ?? "—"}</td>
                      <td><span className={`chip${meta.cls ? ` ${meta.cls}` : ""}`}><span className="material-symbols-sharp">{meta.icon}</span>{meta.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {batch && rows.length > 0 && (
        <div className="sec">
          <div className="sec-hd">
            <h3>Rows</h3>
            <span className="note">
              {batch.summary.unresolvedSuppliers > 0 && `${batch.summary.unresolvedSuppliers} supplier(s) unresolved. `}
              {batch.summary.unresolvedTradeNames > 0 && `${batch.summary.unresolvedTradeNames} trade name(s) unresolved.`}
            </span>
            <div className="seg" style={{ marginLeft: "auto" }}>
              {(["All", "Valid", "Warning", "Error", "Excluded"] as const).map((s) => (
                <button key={s} aria-pressed={rowState === s} onClick={() => changeRowState(s)}>{s}</button>
              ))}
            </div>
          </div>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <table>
              <thead><tr><th>#</th><th>Supplier</th><th>Trade name</th><th>Date</th><th>Qty</th><th>Price</th><th>State</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.sourceRowNumber}>
                    <td>{r.sourceRowNumber}</td>
                    <td>
                      {r.resolved.supplierNameRaw ?? "—"}
                      {!r.resolved.supplierId && r.resolved.supplierNameRaw && (
                        <div style={{ color: "var(--salmon)", fontSize: 11.5, marginTop: 3 }}>unresolved</div>
                      )}
                    </td>
                    <td>{r.resolved.tradeNameRaw ?? "—"}</td>
                    <td>{r.resolved.invoiceDate ?? "—"}</td>
                    <td>{r.resolved.quantity ?? "—"}</td>
                    <td>{r.resolved.unitPrice ?? "—"}</td>
                    <td>
                      {r.validationState === "Valid" ? <span className="chip ok">Valid</span> : <span className="chip bad">{r.validationState}</span>}
                      {r.issues.length > 0 && (
                        <div style={{ color: "var(--salmon)", fontSize: 11.5, marginTop: 3 }}>
                          {r.issues.map((i) => i.message).join("; ")}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="gate">
            <span className="msg">
              {batch.status === "Committed"
                ? "Committed. These rows are now in the production spend table."
                : batch.status === "Committing"
                  ? "Committing…"
                  : batch.canCommit
                    ? `${batch.summary.validRows + batch.summary.warningRows} row(s) will be committed.`
                    : batch.blockers.map((b) => b.message).join(" ")}
            </span>
            <button
              className="btn ghost"
              disabled={batch.status === "Committed" || batch.status === "Committing" || !canManage(batch.createdByAppUserId)}
              title={!canManage(batch.createdByAppUserId) ? "You can only manage data you uploaded, unless you're an Admin" : undefined}
              onClick={discard}
            >
              <span className="material-symbols-sharp">delete</span>Discard Batch
            </button>
            <button
              className="btn"
              disabled={
                batch.status !== "Review" ||
                committing ||
                batch.summary.errorRows === batch.summary.totalRows ||
                !canManage(batch.createdByAppUserId)
              }
              title={!canManage(batch.createdByAppUserId) ? "You can only manage data you uploaded, unless you're an Admin" : undefined}
              onClick={commit}
            >
              <span className="material-symbols-sharp">{batch.status === "Committed" ? "check" : "conveyor_belt"}</span>
              {batch.status === "Committed" ? "Committed" : committing || batch.status === "Committing" ? "Committing…" : "Commit"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
